import React, { Component, ErrorInfo, ReactNode } from 'react';
import { handleReactError } from '../utils/errorLogger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Component name for better error context */
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Use production-ready error logger instead of console.error
    handleReactError(error, {
      componentStack: errorInfo.componentStack || 'Unknown',
    }, {
      component: this.props.componentName || 'Unknown Component',
      errorBoundary: true,
    });
    
    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary" style={{
          padding: '2rem',
          border: '2px solid #ff4444',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          margin: '1rem',
        }}>
          <h2 style={{ color: '#cc0000', marginBottom: '1rem' }}>
            ⚠️ Something went wrong
          </h2>
          <p style={{ marginBottom: '1rem' }}>
            We're sorry, but an unexpected error occurred in this section. 
            The rest of the application should continue to work normally.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <details style={{ 
              whiteSpace: 'pre-wrap', 
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Error Details (Development Only)
              </summary>
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Error:</strong>
                <pre style={{ marginTop: '0.5rem', overflow: 'auto' }}>
                  {this.state.error?.toString()}
                </pre>
              </div>
              <div style={{ marginTop: '1rem' }}>
                <strong>Component Stack:</strong>
                <pre style={{ marginTop: '0.5rem', overflow: 'auto' }}>
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            </details>
          )}
          <button 
            onClick={this.handleReset}
            style={{ 
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
